import { CollectionHeroWidget } from 'consumer-page-widgets/CollectionHeroWidget';
import { FunctionComponent } from 'react';
import { LocalDate, LocalTime } from 'js-joda';
import { SearchQueryStringParams } from 'components/search/utils';
import { WidgetConsumerPageContent } from 'components/www/WidgetConsumerPageContent';
import { WidgetContainer } from 'consumer-page-widgets/WidgetContainer';
import { getTimeParam } from 'utils/timeUtils';
import { gridContainer, heroWidget, topSectionBackdrop } from 'routes/www/wineryRouteCssDefs';
import { stringify } from '@tock/query-string';
import { useLocation } from 'react-router';
import RegionGrid from 'components/wineries/RegionGrid';
import WidgetLayoutContainer from 'consumer-page-widgets/WidgetUtils/WidgetLayoutContainer';

const primaryColor = '#64002C';
const secondaryColor = '#86003A';
const title = 'Wineries &amp; Tasting Rooms';
// the last &nbsp; is to control widows and orphans.
const content =
  'Taste your way through thousands of varietals and vintages all around the&nbsp;world.';
const heroImage =
  'https://storage.googleapis.com/tock-public-assets/consumer-content-widgets/WineriesHero.jpg';

const getQueryString = (queryStringParams: SearchQueryStringParams, city: string) => {
  return stringify({
    ...queryStringParams,
    city,
  });
};

const getBaseUrl = (citySlug: string) => {
  return `/city/${citySlug}/search?`;
};

interface Region {
  href: string;
  img: string;
  region: string;
  state: string;
}

const WineryRoute: FunctionComponent = () => {
  const location = useLocation();

  const queryStringParams: SearchQueryStringParams = {
    date: LocalDate.now().toString(),
    dateEnd: LocalDate.now().plusDays(7).toString(),
    size: '2',
    time: getTimeParam(LocalTime.now()),
    businessType: 'winery',
    displayTerm: 'wineries',
    tock_medium: 'search_nav',
    tock_source: 'tock',
  };

  const CA = 'California';
  const CAN = 'Canada';
  const MI = 'Michigan';
  const OR = 'Oregon';
  const WA = 'Washington';
  const VA = 'Virginia';
  const TX = 'Texas';
  const NY = 'New York';

  const regions: Region[] = [
    {
      href: `${getBaseUrl('napa-valley')}${getQueryString(queryStringParams, 'Napa Valley')}`,
      img: 'https://storage.googleapis.com/tock-public-assets/consumer-content-widgets/winery-regions/napa-valley.jpg',
      region: 'Napa Valley',
      state: CA,
    },
    {
      href: `${getBaseUrl('sonoma-county')}${getQueryString(queryStringParams, 'Sonoma County')}`,
      img: 'https://storage.googleapis.com/tock-public-assets/consumer-content-widgets/winery-regions/sonoma-county.jpg',
      region: 'Sonoma County',
      state: CA,
    },
    {
      href: `${getBaseUrl('paso-robles')}${getQueryString(
        queryStringParams,
        'Paso Robles — San Luis Obispo'
      )}`,
      img: 'https://storage.googleapis.com/tock-public-assets/consumer-content-widgets/winery-regions/paso-robles.jpg',
      region: 'Paso Robles',
      state: CA,
    },
    {
      href: `${getBaseUrl('santa-barbara')}${getQueryString(queryStringParams, 'Santa Barbara')}`,
      img: 'https://storage.googleapis.com/tock-public-assets/consumer-content-widgets/winery-regions/santa-barbara.jpg',
      region: 'Santa Barbara',
      state: CA,
    },
    {
      href: `${getBaseUrl('willamette-valley')}${getQueryString(
        queryStringParams,
        'Willamette Valley'
      )}`,
      img: 'https://storage.googleapis.com/tock-public-assets/consumer-content-widgets/winery-regions/willamette-valley.jpg',
      region: 'Willamette Valley',
      state: OR,
    },
    {
      href: `${getBaseUrl('okanagan-similkameen')}${getQueryString(
        queryStringParams,
        'Okanagan Valley — Similkameen Valley'
      )}`,
      img: 'https://storage.googleapis.com/tock-public-assets/consumer-content-widgets/winery-regions/okanagan-valley.jpg',
      region: 'Okanagan Valley',
      state: CAN,
    },
    {
      href: `${getBaseUrl('sacramento')}${getQueryString(queryStringParams, 'Sacramento')}`,
      img: 'https://storage.googleapis.com/tock-public-assets/consumer-content-widgets/winery-regions/sacramento.jpg',
      region: 'Sacramento',
      state: CA,
    },
    {
      href: `${getBaseUrl('anderson-valley')}${getQueryString(
        queryStringParams,
        'Anderson Valley'
      )}`,
      img: 'https://storage.googleapis.com/tock-public-assets/consumer-content-widgets/winery-regions/anderson-valley.jpg',
      region: 'Anderson Valley',
      state: CA,
    },
    {
      href: `${getBaseUrl('walla_walla')}${getQueryString(queryStringParams, 'Walla Walla')}`,
      img: 'https://storage.googleapis.com/tock-public-assets/consumer-content-widgets/winery-regions/walla-walla.jpg',
      region: 'Walla Walla',
      state: WA,
    },
    {
      href: `${getBaseUrl('san-jose')}${getQueryString(queryStringParams, 'San Jose')}`,
      img: 'https://storage.googleapis.com/tock-public-assets/consumer-content-widgets/winery-regions/san-jose.jpg',
      region: 'San Jose',
      state: CA,
    },
    {
      href: `${getBaseUrl('woodinville')}${getQueryString(queryStringParams, 'Woodinville')}`,
      img: 'https://storage.googleapis.com/tock-public-assets/consumer-content-widgets/winery-regions/woodinville.jpg',
      region: 'Woodinville',
      state: WA,
    },
    {
      href: `${getBaseUrl('traverse-city')}${getQueryString(queryStringParams, 'Traverse City')}`,
      img: 'https://storage.googleapis.com/tock-public-assets/consumer-content-widgets/winery-regions/traverse-city.jpg',
      region: 'Traverse City',
      state: MI,
    },
    {
      href: `${getBaseUrl('kelowna')}${getQueryString(queryStringParams, 'Kelowna')}`,
      img: 'https://storage.googleapis.com/tock-public-assets/imagesprod/user_uploaded_images_v1/3bb456e5ac45c6a032165580346c6e90.jpg',
      region: 'Kelowna',
      state: CAN,
    },
    {
      href: `${getBaseUrl('fairfax')}${getQueryString(queryStringParams, 'Fairfax')}`,
      img: 'https://storage.googleapis.com/tock-public-assets/imagesprod/user_uploaded_images_v1/a8513218275433a6e12c580e165609f7.jpg',
      region: 'Fairfax',
      state: VA,
    },
    {
      href: `${getBaseUrl('austin')}${getQueryString(queryStringParams, 'Austin')}`,
      img: 'https://storage.googleapis.com/tock-public-assets/imagesprod/user_uploaded_images_v1/b96961ad085ef49f10d3376cb2b118ca.jpg',
      region: 'Austin',
      state: TX,
    },
    {
      href: `${getBaseUrl('finger-lakes')}${getQueryString(queryStringParams, 'Finger Lakes')}`,
      img: 'https://storage.googleapis.com/tock-public-assets/imagesprod/user_uploaded_images_v1/7e77809773994df64e408cdebba76def.jpg',
      region: 'Finger Lakes',
      state: NY,
    },
    {
      href: `${getBaseUrl('niagara-falls')}${getQueryString(queryStringParams, 'Niagara Falls')}`,
      img: 'https://storage.googleapis.com/tock-public-assets/imagesprod/user_uploaded_images_v1/5f235ec0bf530fdae65304ac332ac093.jpg',
      region: 'Niagara Falls',
      state: CAN,
    },
    {
      href: `${getBaseUrl('fredericksburg')}${getQueryString(queryStringParams, 'Fredericksburg')}`,
      img: 'https://storage.googleapis.com/tock-public-assets/imagesprod/user_uploaded_images_v1/140e191db0119399821a466e4d7434ea.jpg',
      region: 'Fredericksburg',
      state: TX,
    },
    {
      href: `${getBaseUrl('long-island')}${getQueryString(queryStringParams, 'Long Island')}`,
      img: 'https://storage.googleapis.com/tock-public-assets/imagesprod/user_uploaded_images_v1/7d62bf7463e23dadef56cc47cef4dfb0.jpg',
      region: 'Long Island',
      state: NY,
    },
    {
      href: `${getBaseUrl('washington-dc')}${getQueryString(queryStringParams, 'Washington D.C.')}`,
      img: 'https://storage.googleapis.com/tock-public-assets/imagesprod/user_uploaded_images_v1/f1cbb2a2349a24e21f765539ccc5ac49.jpg',
      region: 'Washington D.C.',
      state: VA,
    },
    {
      href: `${getBaseUrl('pentiction')}${getQueryString(queryStringParams, 'Penticton')}`,
      img: 'https://storage.googleapis.com/tock-public-assets/imagesprod/user_uploaded_images_v1/35a60a0460f08fddd093921532d8f5b0.jpg',
      region: 'Penticton',
      state: CAN,
    },
    {
      href: `${getBaseUrl('lake-county')}${getQueryString(queryStringParams, 'Lake County')}`,
      img: 'https://storage.googleapis.com/tock-public-assets/imagesprod/user_uploaded_images_v1/d8b36b44-9baf-4e31-87c9-8c7bf0285190.jpg',
      region: 'Lake County',
      state: CA,
    },
  ];

  return (
    <div className="PageContent PageContent--page-widget">
      <div css={topSectionBackdrop}>
        <CollectionHeroWidget
          css={heroWidget}
          primaryColor={primaryColor}
          secondaryColor={secondaryColor}
          title={title}
          isPageTitle={true}
          content={content}
          heroImage={heroImage}
        />
        <WidgetLayoutContainer css={gridContainer}>
          <WidgetContainer title="Featured regions" isTitle={false} contains="Winery page">
            <RegionGrid regions={regions} />
          </WidgetContainer>
        </WidgetLayoutContainer>
      </div>
      <WidgetConsumerPageContent pathname={location.pathname} standAlone={false} />
    </div>
  );
};

export default WineryRoute;
